import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { Music24QuotaKind } from 'fym-common/src/license/music24Plans';
import type { UpsellSurface } from 'fym-shared/src/music24/watermarkUpsell';
import { TypedMemo } from '../../../common/types';
import { toggleSMLPricingModal } from '../../../../redux/slices/appInfo/slice';
import { SMLUpgradeFeature, planUnlocksFeature } from '../../config/music24PlanCapabilities';
import { SMLUpgradeOffer } from './SMLUpgradeOffer';
import { SMLUpgradeSuccess } from './SMLUpgradeSuccess';
import { useUpgradeOffer } from './useUpgradeOffer';
import { WatermarkOffer } from '../../watermarkUpsell/offer/WatermarkOffer';
import { useWatermarkOfferPlan } from '../../watermarkUpsell/offer/useWatermarkOfferPlan';
import { UPSELL_MODAL_Z_INDEX } from '../../watermarkUpsell/upsellModalLayer';
import styles from './SMLUpgradeModal.module.scss';

export interface SMLUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** What the person just reached for. Omit it and the modal makes the generic, priced offer. */
  feature?: SMLUpgradeFeature;
  /** The name of the thing they clicked — an artist, a track, a playlist. */
  subject?: string;
  /** The range they asked for, when the trigger is a locked history chip. */
  requiredHistoryDays?: number;
  /**
   * Set together for an `M24_LIMIT_QUOTA` prompt (`GlobalSMLUpgradeModal` reads them off
   * `smlUpgradePrompt`). The offer view then shows design b2's meter above the plans.
   */
  limit?: number | null;
  used?: number | null;
  kind?: Music24QuotaKind | null;
  resetsAt?: string | null;
  /** Only for `watermark`: the surface that opened the offer (the checkout metadata, the events). */
  upsellSurface?: UpsellSurface;
}

type UpgradeView = 'offer' | 'success';

const SMLUpgradeModalComponent: React.FC<SMLUpgradeModalProps> = ({
  isOpen,
  onClose,
  feature = SMLUpgradeFeature.generic,
  subject,
  requiredHistoryDays,
  limit,
  used,
  kind,
  resetsAt,
  upsellSurface,
}) => {
  const dispatch = useDispatch();
  const isWatermark = feature === SMLUpgradeFeature.watermark;
  // design: trial-upsell b4. The offer selects Artist, or Pro when a watermarked file needs it.
  const defaultPlanId = useWatermarkOfferPlan(isWatermark);
  const offer = useUpgradeOffer(feature, {
    requiredHistoryDays,
    defaultPlanId,
    upsellSurface: isWatermark ? (upsellSurface ?? 'offer') : undefined,
  });
  const [view, setView] = useState<UpgradeView>('offer');
  const wasLocked = useRef(false);

  const isUnlocked =
    feature === SMLUpgradeFeature.generic
      ? offer.hasPaidPlan
      : !planUnlocksFeature(offer.planCapabilities, feature, offer.currentCapabilities);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setView((current) => (current === 'success' ? current : 'offer'));
    // Each open starts on the cheapest plan again (design b).
    offer.selectPlan(null);
    wasLocked.current = !isUnlocked;
    // Only on open: the effect must not reset the view or the chosen plan while the modal is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // The licence can turn valid while the modal is open — the customer portal, a trial grant, or a
  // checkout finished in another tab. Say so, and offer the way back to the blocked view.
  useEffect(() => {
    if (isOpen && isUnlocked && wasLocked.current) {
      wasLocked.current = false;
      setView('success');
    }
  }, [isOpen, isUnlocked]);

  const seeAllPlans = useCallback(() => {
    onClose();
    dispatch(toggleSMLPricingModal({ shouldShow: true }));
  }, [onClose, dispatch]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      scrollable
      // The watermark offer opens from the asset viewer too, which sits above the modal layer.
      zIndex={isWatermark ? UPSELL_MODAL_Z_INDEX : undefined}
      className={styles.dialog}
      contentClassName={styles.content}
    >
      {view === 'offer' && isWatermark && (
        <WatermarkOffer offer={offer} surface={upsellSurface ?? 'offer'} onClose={onClose} onCompare={seeAllPlans} />
      )}
      {view === 'offer' && !isWatermark && (
        <SMLUpgradeOffer
          feature={feature}
          subject={subject}
          offer={offer}
          onClose={onClose}
          onCompare={seeAllPlans}
          limit={limit}
          used={used}
          kind={kind}
          resetsAt={resetsAt}
        />
      )}
      {view === 'success' && <SMLUpgradeSuccess feature={feature} offer={offer} onClose={onClose} />}
    </Modal>
  );
};

export const SMLUpgradeModal = TypedMemo(SMLUpgradeModalComponent);
